import {inject, injectable} from "inversify";
import {AchievementHandlerInterface, AchievementInterface} from "@/modules/archiements/interfaces";
import {Achievement} from "@/modules/archiements/entity";
import {AchievementRewardsType} from "@/modules/archiements/types";
import {EventEmitter} from "@/modules/base/events";
import BeforeAchievementAchievedEvent from "@/modules/archiements/events/BeforeAchievementAchievedEvent";
import AfterAchievementAchievedEvent from "@/modules/archiements/events/AfterAchievementAchievedEvent";
import {AchievementTypeEnum} from "../enums";
import {persistDataSource} from "@/modules/database/services/db";
import {db} from "@/modules/main-menu/services/db";

@injectable()
export default class LocalAchievementHandler implements AchievementHandlerInterface {
  constructor(
    @inject(EventEmitter) private em: EventEmitter,
  ) {}

  async save(achievement: AchievementInterface): Promise<void> {
    await (achievement as Achievement).save();
    await persistDataSource(db);
  }

  async seeded() {
    return (await this.all()).length > 0;
  }

  async all() {
    return await Achievement
      .createQueryBuilder('a')
      .getMany();
  }

  async create(
    type: AchievementTypeEnum,
    neededValue: number,
    rewards: AchievementRewardsType
  ): Promise<AchievementInterface> {
    const achievement = new Achievement();
    achievement.type = type;
    achievement.neededValue = neededValue;
    achievement.rewards = rewards;
    await achievement.save();

    await persistDataSource(db);

    return achievement;
  }

  private async handleAchievement(a: Achievement) {
    a.achieved = a.currentValue >= a.neededValue;

    if (a.achieved) {
      await this.em.emitAsync(new BeforeAchievementAchievedEvent(a));
    }

    await a.save();

    if (a.achieved) {
      await this.em.emitAsync(new AfterAchievementAchievedEvent(a));
    }
  }

  private async getItems(type: AchievementTypeEnum) {
    return await Achievement
      .createQueryBuilder('achievement')
      .where('achievement.achieved = false AND achievement.type = :achievementType', {achievementType: type})
      .getMany();
  }

  async setHighest(type: AchievementTypeEnum, value: number): Promise<void> {
    const items = await this.getItems(type);
    await items
      .filter((a: Achievement) => a.currentValue <= value)
      .forEachAsync(async (a: Achievement) => {
        a.currentValue = value;
        await this.handleAchievement(a);
      });

    await persistDataSource(db);
  }

  async increase(type: AchievementTypeEnum, step: number): Promise<void> {
    const items = await this.getItems(type);
    await items.forEachAsync(async (a: Achievement) => {
      a.currentValue += step;
      await this.handleAchievement(a);
    });

    await persistDataSource(db);
  }
}
